/**
 * KAN-744: справочник менеджеров: список, карточка, воронка менеджера,
 * правки (только владелец) и закрепление за клиентом и заказом.
 * После любой правки обновляются список и карточки менеджеров и клиенты.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isManagerUser } from '@/lib/managerAccess';
import { managersApi } from '@/services/api/managers';
import { MANAGERS_QUERY_KEY, ORDERS_QUERY_KEY } from '@/services/api/queryKeys';
import { clientManagerChangedKeys, invalidateKeys } from '@/services/api/clientEditKeys';
import type { FunnelStatsChannel, FunnelStatsPeriod } from '@/types/funnel';
import type { ManagerActiveFilter, ManagerBody, ManagerBrief } from '@/types/manager';

export const managersListKey = (active: ManagerActiveFilter) =>
  [...MANAGERS_QUERY_KEY, 'list', active] as const;
export const managerDetailKey = (id: string | undefined) =>
  [...MANAGERS_QUERY_KEY, 'detail', id] as const;
export const managerFunnelKey = (id: string) => [...MANAGERS_QUERY_KEY, 'funnel', id] as const;

export function useManagers(active: ManagerActiveFilter = 'true') {
  return useQuery({
    queryKey: managersListKey(active),
    queryFn: () => managersApi.list(active),
    staleTime: 30_000,
  });
}

export interface AssignableManagers {
  data: ManagerBrief[];
  isLoading: boolean;
}

const NO_ASSIGNABLE: ManagerBrief[] = [];

/**
 * KAN-811/KAN-828: кого пользователь может назначить клиенту (одно правило для
 * всех выборов менеджера). Все роли видят всех активных менеджеров: менеджер
 * может передать клиента другому. Кому из клиентов менять менеджера можно,
 * решает clientManagerRights (lib/clientManagerRights.ts). Если менеджера нет
 * в списке (пустой справочник), он добавляется из /auth/me.
 */
export function useAssignableManagers(): AssignableManagers {
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const { data: managers, isLoading: listLoading } = useManagers('true');
  const isManager = isManagerUser(user);
  const selfId = user?.manager_id ?? null;
  const selfName = user?.manager_name ?? '';

  const data = useMemo<ManagerBrief[]>(() => {
    const list = managers ?? NO_ASSIGNABLE;
    if (!isManager || !selfId || list.some((m) => m.id === selfId)) return list;
    return [...list, { id: selfId, full_name: selfName }];
  }, [isManager, managers, selfId, selfName]);

  return { data, isLoading: userLoading || listLoading };
}

export function useManager(id: string | undefined) {
  return useQuery({
    queryKey: managerDetailKey(id),
    queryFn: () => managersApi.get(id as string),
    enabled: !!id,
  });
}

/** Загрузчик воронки менеджера для общей карточки воронки. */
export function managerFunnelLoader(id: string) {
  return (period: FunnelStatsPeriod, channel: FunnelStatsChannel) =>
    managersApi.getFunnel(id, period, channel);
}

function useInvalidatingMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: MANAGERS_QUERY_KEY });
      void client.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useManagerMutations() {
  return {
    create: useInvalidatingMutation((data: ManagerBody) => managersApi.create(data)),
    update: useInvalidatingMutation((args: { id: string; data: ManagerBody }) =>
      managersApi.update(args.id, args.data)
    ),
    remove: useInvalidatingMutation((id: string) => managersApi.remove(id)),
  };
}

/**
 * Закрепить менеджера за клиентом (null: снять); сохраняется сразу.
 * Ошибку (400 manager_id, 403) показывает селект под собой.
 * KAN-828: менеджер ставится и из строки блока "Требуют внимания" (строка
 * остаётся), поэтому обновляется и блок внимания (в его ключ вложена доска CRM).
 */
export function useAssignClientManager(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (managerId: string | null) => managersApi.assignToClient(clientId, managerId),
    onSettled: () => {
      void invalidateKeys(client, clientManagerChangedKeys(clientId));
    },
  });
}

/**
 * Закрепить менеджера за заказом (null: снять); сохраняется сразу.
 * KAN-746: заказ показывается и в разделе "Заказы", его кэш тоже обновляется.
 */
export function useAssignEstimateManager(estimateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (managerId: string | null) => managersApi.assignToEstimate(estimateId, managerId),
    onSettled: () => {
      for (const queryKey of [['estimate', estimateId], ['estimates'], ORDERS_QUERY_KEY, MANAGERS_QUERY_KEY]) {
        void client.invalidateQueries({ queryKey });
      }
    },
  });
}
